import React from "react";
import { Box, Chip, Divider, Grid2, Paper, useTheme } from "@mui/material";
import type { Theme } from "@mui/material/styles";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import { formatDateWithoutTime } from "../../../../../../views/components/common/functions/formatDate";
import {
  DailyTasksDetailResult,
  DailyTasksSlaStatus,
  PR_WORK_STATE_LABELS,
  VARIANTS_COVERAGE_LABELS,
  pendingApprovalLabels,
  prOpenWorkStates,
  variantsCoverage,
  PurchaseRequestDrawerDto,
} from "../../../../../../models/Dashboard/DailyTasks/DailyTasks";
import {
  getModernPageColors,
  modernSectionPaperSx,
} from "../../../../../../styles/modernListingPageSx";
import { dailyTasksChipSx, getDailyTasksChipColors } from "./dailyTasksChips";
import { IdLink, purchaseRequestDetailsPath } from "./DailyTasksMedia";
import { useDailyTasksFieldPermissions } from "./useDailyTasksFieldPermissions";
import {
  DailyTasksEmptyState,
  DailyTasksSectionHeader,
} from "./DailyTasksEmptyState";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";

interface PrDrawerBodyProps {
  detail: DailyTasksDetailResult;
}

const formatDate = (value: string | Date | null | undefined): string => {
  if (!value) return "-";
  return formatDateWithoutTime(value) || "-";
};

const formatHours = (hours: number | null | undefined): string => {
  if (hours == null || !Number.isFinite(Number(hours))) return "-";
  const value = Math.round(Number(hours));
  if (value < 24) return `${value}h`;
  const days = Math.floor(value / 24);
  const rem = value % 24;
  return rem ? `${days}d ${rem}h` : `${days}d`;
};

const personLine = (date: string | Date | null | undefined, name: string | null) => {
  const when = formatDate(date);
  if (when === "-") return name || "Pending";
  return name ? `${when} · ${name}` : when;
};

const slaTone = (
  theme: Theme,
  status: DailyTasksSlaStatus,
): { bg: string; fg: string; label: string } => {
  try {
    if (status === "bad") {
      return { ...getDailyTasksChipColors(theme, "bad"), label: "Overdue" };
    }
    if (status === "warn") {
      return { ...getDailyTasksChipColors(theme, "warn"), label: "Watch" };
    }
    if (status === "ok") {
      return { ...getDailyTasksChipColors(theme, "ok"), label: "On track" };
    }
    return { ...getDailyTasksChipColors(theme, "info"), label: "Pending" };
  } catch (error) {
    return { ...getDailyTasksChipColors(theme, "info"), label: "Pending" };
  }
};

const StatTile: React.FC<{
  eyebrow: string;
  value: string;
  hint?: string;
  pill?: { label: string; bg: string; fg: string };
}> = ({ eyebrow, value, hint, pill }) => {
  const theme = useTheme();
  const colors = theme.palette.customColors;
  return (
    <Box sx={{ height: "100%" }}>
      <CustomTypography
        variant="caption"
        fontWeight={600}
        sx={{
          color: colors.greyTextColor,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
        }}
      >
        {eyebrow}
      </CustomTypography>
      <CustomTypography
        variant="heading5"
        fontWeight={600}
        sx={{
          color: colors.navyText,
          letterSpacing: "-0.02em",
          lineHeight: 1.1,
          mt: 0.5,
        }}
      >
        {value}
      </CustomTypography>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 0.75,
          mt: 0.75,
        }}
      >
        {pill ? (
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              px: 1,
              py: 0.25,
              borderRadius: 1,
              bgcolor: pill.bg,
              color: pill.fg,
            }}
          >
            <CustomTypography variant="caption" fontWeight={700} color={pill.fg}>
              {pill.label}
            </CustomTypography>
          </Box>
        ) : null}
        {hint ? (
          <CustomTypography variant="caption" sx={{ color: colors.greyTextColor }}>
            {hint}
          </CustomTypography>
        ) : null}
      </Box>
    </Box>
  );
};

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const theme = useTheme();
  return (
    <CustomTypography
      variant="caption"
      fontWeight={600}
      sx={{
        color: getModernPageColors(theme).subtitle,
        letterSpacing: "0.04em",
        textTransform: "uppercase",
        display: "block",
        mb: 1.25,
      }}
    >
      {children}
    </CustomTypography>
  );
};

const MetaField: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => {
  const theme = useTheme();
  const pageColors = getModernPageColors(theme);
  return (
    <Grid2 size={{ xs: 6, sm: 4 }}>
      <CustomTypography
        variant="caption"
        fontWeight={600}
        sx={{
          color: pageColors.subtitle,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
        }}
      >
        {label}
      </CustomTypography>
      <CustomTypography
        variant="body2"
        fontWeight={500}
        sx={{ color: pageColors.title, mt: 0.25 }}
      >
        {value}
      </CustomTypography>
    </Grid2>
  );
};

const ApprovalStep: React.FC<{
  label: string;
  date: string | Date | null;
  approvedBy: string | null;
  rejectedDate: string | Date | null;
  rejectedBy: string | null;
}> = ({ label, date, approvedBy, rejectedDate, rejectedBy }) => {
  const theme = useTheme();
  const colors = theme.palette.customColors;
  const rejected = Boolean(rejectedDate);
  const approved = Boolean(date) && !rejected;
  const tone = rejected ? "bad" : approved ? "ok" : "neutral";
  const chip = getDailyTasksChipColors(theme, tone);
  const status = rejected ? "Rejected" : approved ? "Approved" : "Pending";
  const detail = rejected
    ? personLine(rejectedDate, rejectedBy)
    : approved
      ? personLine(date, approvedBy)
      : "Not done";

  return (
    <Box
      sx={{
        p: 1.25,
        borderRadius: 1.5,
        bgcolor: colors.veryLightGray,
        height: "100%",
      }}
    >
      <CustomTypography
        variant="caption"
        fontWeight={600}
        sx={{
          color: colors.greyTextColor,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
          display: "block",
        }}
      >
        {label}
      </CustomTypography>
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          px: 0.75,
          py: 0.15,
          mt: 0.5,
          mb: 0.5,
          borderRadius: 1,
          bgcolor: chip.bg,
          color: chip.fg,
        }}
      >
        <CustomTypography variant="caption" fontWeight={700} color={chip.fg}>
          {status}
        </CustomTypography>
      </Box>
      <CustomTypography
        variant="body3"
        fontWeight={500}
        sx={{ color: colors.navyText, display: "block" }}
      >
        {detail}
      </CustomTypography>
    </Box>
  );
};

const PrCard: React.FC<{
  request: PurchaseRequestDrawerDto;
  orderedQuantity: number;
  orderId: number;
}> = ({ request, orderedQuantity, orderId }) => {
  const theme = useTheme();
  const colors = theme.palette.customColors;
  const cancelled = Boolean(request.pr_cancellation_date);
  const showSwageazy = Boolean(request.swageazy_approval_required);
  const stepSize = showSwageazy ? { xs: 12, sm: 4 } : { xs: 12, sm: 6 };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.75,
        border: `1px solid ${colors.softBorder}`,
        borderRadius: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          alignItems: "flex-start",
          justifyContent: "space-between",
          mb: 1.25,
        }}
      >
        <Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "baseline",
              flexWrap: "wrap",
              gap: 0.5,
            }}
          >
            <IdLink
              href={purchaseRequestDetailsPath(request.id, orderId)}
              label={`PR #${request.id}`}
              fontSize="0.875rem"
            />
            <CustomTypography variant="body2" fontWeight={600}>
              · {request.vendor_name || "No vendor"}
            </CustomTypography>
          </Box>
          <CustomTypography
            variant="caption"
            sx={{ color: colors.greyTextColor, display: "block", mt: 0.25 }}
          >
            Qty {request.quantity}
            {orderedQuantity > 0 ? ` of ${orderedQuantity}` : ""}
            {request.created_by
              ? ` · Created ${formatDate(request.created_date)} by ${request.created_by}`
              : ` · Created ${formatDate(request.created_date)}`}
          </CustomTypography>
        </Box>
        <Chip
          size="small"
          label={
            cancelled
              ? "Cancelled"
              : request.is_fully_approved
                ? "Approved"
                : request.is_clean
                  ? "Approval pending"
                  : "Rejected"
          }
          sx={dailyTasksChipSx(
            theme,
            cancelled || !request.is_clean
              ? "bad"
              : request.is_fully_approved
                ? "ok"
                : "warn",
          )}
        />
      </Box>

      <Grid2 container spacing={1} columns={12}>
        <Grid2 size={stepSize}>
          <ApprovalStep
            label="Sourcing"
            date={request.sourcing_approval_date}
            approvedBy={request.sourcing_approved_by}
            rejectedDate={request.rejection_date_by_sourcing}
            rejectedBy={request.sourcing_rejected_by}
          />
        </Grid2>
        <Grid2 size={stepSize}>
          <ApprovalStep
            label="Finance"
            date={request.finance_approval_date}
            approvedBy={request.finance_approved_by}
            rejectedDate={request.rejection_date_by_finance}
            rejectedBy={request.finance_rejected_by}
          />
        </Grid2>
        {showSwageazy ? (
          <Grid2 size={stepSize}>
            <ApprovalStep
              label="Swageazy"
              date={request.swageazy_approval_date}
              approvedBy={request.swageazy_approved_by}
              rejectedDate={request.rejection_date_by_swageazy}
              rejectedBy={request.swageazy_rejected_by}
            />
          </Grid2>
        ) : null}
      </Grid2>

      {cancelled ? (
        <CustomTypography
          variant="body3"
          fontWeight={500}
          sx={{ color: colors.errorRed, display: "block", mt: 1 }}
        >
          Cancelled {formatDate(request.pr_cancellation_date)}
          {request.cancelled_by ? ` by ${request.cancelled_by}` : ""}
        </CustomTypography>
      ) : null}
    </Paper>
  );
};

const PrDrawerBody: React.FC<PrDrawerBodyProps> = ({ detail }) => {
  const theme = useTheme();
  const colors = theme.palette.customColors;
  const { canViewDispatchDate } = useDailyTasksFieldPermissions();
  const sla = slaTone(theme, detail.sla_status);
  const daysHint =
    detail.days_to_sla == null
      ? null
      : detail.days_to_sla < 0
        ? `${Math.abs(detail.days_to_sla)} day${
            Math.abs(detail.days_to_sla) === 1 ? "" : "s"
          } past SLA`
        : detail.days_to_sla === 0
          ? "Due today"
          : `${detail.days_to_sla} day${detail.days_to_sla === 1 ? "" : "s"} left`;
  const pendingNames = pendingApprovalLabels(detail);
  const workStates = prOpenWorkStates(detail);
  const clockHint =
    workStates.includes("pending_approval")
      ? "Since oldest PR waiting approval"
      : "Since project approved";
  const requests = detail.purchase_requests ?? [];
  const approvedQty = detail.pr_qty ?? 0;
  const orderedQty = detail.ordered_quantity ?? 0;
  const qtyPct =
    orderedQty > 0 ? Math.min(100, Math.round((approvedQty / orderedQty) * 100)) : 0;
  const remaining = Math.max(orderedQty - approvedQty, 0);

  return (
    <>
      <Paper elevation={0} sx={modernSectionPaperSx(theme)}>
        <Grid2 container spacing={2} columns={12}>
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <StatTile
              eyebrow="Elapsed"
              value={formatHours(detail.sla_elapsed_hours)}
              hint={`Since ${formatDate(detail.sla_clock_start)}`}
              pill={{ label: sla.label, bg: sla.bg, fg: sla.fg }}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <StatTile
              eyebrow="PR SLA"
              value={formatHours(detail.sla_target_hours)}
              hint={daysHint ?? clockHint}
            />
          </Grid2>
        </Grid2>
        <Box sx={{ mt: 2, display: "flex", flexWrap: "wrap", gap: 1 }}>
          {workStates.map((state) => (
            <Chip
              key={state}
              size="small"
              label={PR_WORK_STATE_LABELS[state]}
              sx={dailyTasksChipSx(
                theme,
                state === "needs_pr"
                  ? "bad"
                  : state === "pending_approval"
                    ? "warn"
                    : "info",
              )}
            />
          ))}
          {workStates.length === 0 ? (
            <Chip
              size="small"
              label="Full qty"
              sx={dailyTasksChipSx(theme, "ok")}
            />
          ) : null}
          {pendingNames.map((name) => (
            <Chip
              key={name}
              size="small"
              label={`${name} pending`}
              sx={dailyTasksChipSx(theme, "warn")}
            />
          ))}
          <CustomTypography
            variant="caption"
            sx={{ color: colors.greyTextColor, alignSelf: "center" }}
          >
            {clockHint}
          </CustomTypography>
        </Box>
        <Box sx={{ mt: 1.75 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              gap: 1,
              mb: 0.75,
            }}
          >
            <CustomTypography variant="caption" fontWeight={600} sx={{ color: colors.greyTextColor }}>
              Approved qty
            </CustomTypography>
            <CustomTypography variant="caption" fontWeight={600} sx={{ color: colors.navyText }}>
              {approvedQty} / {orderedQty}
              {remaining > 0 ? ` · ${remaining} more needed` : ""}
            </CustomTypography>
          </Box>
          <Box
            sx={{
              height: 6,
              borderRadius: 99,
              bgcolor: colors.veryLightGray,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                width: `${qtyPct}%`,
                height: "100%",
                bgcolor:
                  remaining === 0 ? colors.successGreen : colors.darkBlue,
              }}
            />
          </Box>
        </Box>
      </Paper>

      <Paper elevation={0} sx={{ ...modernSectionPaperSx(theme), mt: 2 }}>
        <SectionLabel>Order</SectionLabel>
        <Grid2 container spacing={1.5} columns={12}>
          <MetaField label="Type" value={detail.order_type || "-"} />
          <MetaField label="Qty" value={String(orderedQty)} />
          <MetaField label="Vendor" value={detail.pr_vendor_name || "-"} />
          <MetaField
            label="Variants"
            value={`${detail.variants_qty ?? 0}/${orderedQty} · ${
              VARIANTS_COVERAGE_LABELS[
                variantsCoverage(detail.variants_qty, orderedQty)
              ]
            }`}
          />
          <MetaField label="First PR" value={formatDate(detail.first_pr_at)} />
          <MetaField label="Full qty" value={formatDate(detail.covering_pr_at)} />
          <MetaField
            label="Proj finance"
            value={
              detail.project_finance_status
                ? detail.project_finance_status.charAt(0).toUpperCase() +
                  detail.project_finance_status.slice(1)
                : "-"
            }
          />
          <MetaField
            label="Finance approved"
            value={formatDate(detail.project_finance_approval_date)}
          />
          <MetaField label="PM" value={detail.pm_poc || "Unassigned"} />
          <MetaField label="Sales" value={detail.sales_poc || "-"} />
          {canViewDispatchDate && detail.dispatch_date ? (
            <MetaField label="Dispatch" value={formatDate(detail.dispatch_date)} />
          ) : null}
        </Grid2>
        <Divider sx={{ my: 2 }} />
        <SectionLabel>PR clock</SectionLabel>
        <Grid2 container spacing={1.5} columns={12}>
          <MetaField
            label="Proj fin TAT"
            value={formatHours(detail.project_finance_tat_hours)}
          />
          <MetaField
            label="PR TAT"
            value={formatHours(detail.pr_creation_tat_hours)}
          />
          <MetaField
            label="Total TAT"
            value={formatHours(detail.total_tat_hours)}
          />
        </Grid2>
      </Paper>

      <Box sx={{ mt: 3, mb: 2 }}>
        <DailyTasksSectionHeader
          title="Purchase requests"
          count={requests.length}
        />
        {requests.length === 0 ? (
          <DailyTasksEmptyState
            icon={AssignmentOutlinedIcon}
            title="No purchase requests yet"
            description="PRs created for this product will appear here with vendor, qty, and approval status."
            sx={{ mt: 0 }}
          />
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
            {requests.map((request) => (
              <PrCard
                key={request.id}
                request={request}
                orderedQuantity={orderedQty}
                orderId={detail.order_id}
              />
            ))}
          </Box>
        )}
      </Box>
    </>
  );
};

export default PrDrawerBody;
